import api from "./axios";
import type { User, Role } from "@/types/auth";

export type { User, Role };

export interface CreateUserRequest {
    name: string;
    email: string;
    password: string;
    status?: boolean;
    role_ids?: number[];
}

export interface UpdateUserRequest {
    name?: string;
    email?: string;
    password?: string;
    status?: boolean;
    role_ids?: number[];
}

export interface UserFilters {
    search?: string;
    status?: "active" | "inactive" | boolean;
    role?: string;
    email_verified?: "verified" | "unverified";
    date_from?: string;
    date_to?: string;
    page?: number;
    per_page?: number;
}

export const userApi = {
    async listUsers(filters: UserFilters = {}): Promise<{
        success: boolean;
        message: string;
        data: { items: User[]; meta: any };
    }> {
        const response = await api.get("/v1/users", { params: filters });
        return response.data;
    },

    async getUser(id: string): Promise<User> {
        const response = await api.get(`/v1/users/${id}`);
        // Backend returns { success: true, data: user, message: string }
        return response.data.data;
    },

    async createUser(data: CreateUserRequest): Promise<User> {
        const response = await api.post("/v1/users", data);
        // Backend returns { success: true, data: user, message: string }
        return response.data.data;
    },

    async updateUser(id: string, data: UpdateUserRequest): Promise<User> {
        const response = await api.put(`/v1/users/${id}`, data);
        // Backend returns { success: true, data: user, message: string }
        return response.data.data;
    },

    async deleteUser(id: string): Promise<void> {
        await api.delete(`/v1/users/${id}`);
    },

    async assignRole(userId: string, roleId: number): Promise<User> {
        const response = await api.post(`/v1/users/${userId}/roles`, {
            role_id: roleId,
        });
        // Backend returns { success: true, data: user, message: string }
        return response.data.data;
    },

    async removeRole(userId: string, roleId: number): Promise<User> {
        const response = await api.delete(
            `/v1/users/${userId}/roles/${roleId}`,
        );
        // Backend returns { success: true, data: user, message: string }
        return response.data.data;
    },

    async toggleStatus(userId: string): Promise<User> {
        const response = await api.post(`/v1/users/${userId}/toggle-status`);
        // Backend returns { success: true, data: user, message: string }
        return response.data.data;
    },

    async verifyUser(userId: string): Promise<User> {
        const response = await api.post(`/v1/users/${userId}/verify`);
        // Backend returns { success: true, data: user, message: string }
        return response.data.data;
    },

    async unverifyUser(userId: string): Promise<User> {
        const response = await api.post(`/v1/users/${userId}/unverify`);
        // Backend returns { success: true, data: user, message: string }
        return response.data.data;
    },

    // Profile specific methods
    async getProfile(): Promise<{ user: User; profile: any }> {
        const response = await api.get("/v1/profile");
        // Backend returns { success: true, data: { user: User, profile: any }, message: string }
        return response.data.data;
    },

    async updateProfile(
        data: UpdateUserRequest,
    ): Promise<{ user: User; profile: any }> {
        const response = await api.put("/v1/profile", data);
        // Backend returns { success: true, data: { user: User, profile: any }, message: string }
        return response.data.data;
    },
};

export default userApi;
